import { useState } from 'react';
import { Menu, X, Home, Package, Store, Bike, DollarSign, LogOut, User, QrCode, ScanLine, Shield, Navigation, Truck, BarChart3, ClipboardList, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Layout = ({ user, onLogout, title, children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home, roles: ['admin', 'dispensary_owner', 'dispensary_supervisor', 'dispensary_cashier', 'dispensary_preparer', 'rider', 'support', 'customer'] },
    { name: 'Mis Entregas', href: '/rider-dashboard', icon: Truck, roles: ['rider'] },
    { name: 'Panel Admin', href: '/admin', icon: Shield, roles: ['admin', 'support'] },
    { name: 'UID Pulse', href: '/pulse', icon: Activity, roles: ['admin', 'support'] },
    { name: 'Analítica', href: '/analytics', icon: BarChart3, roles: ['admin', 'support', 'dispensary_owner', 'dispensary_supervisor'] },
    { name: 'Preparación', href: '/orders', icon: ClipboardList, roles: ['dispensary_preparer'] },
    { name: 'Pedidos', href: '/orders', icon: Package, roles: ['admin', 'dispensary_owner', 'dispensary_supervisor', 'dispensary_cashier', 'rider', 'support'] },
    { name: 'Crear Pedido', href: '/orders/create', icon: Package, roles: ['dispensary_cashier'] },
    { name: 'Gestión de Rutas', href: '/route-manager', icon: Navigation, roles: ['admin', 'support'] },
    { name: 'Optimizar Rutas', href: '/route-optimization', icon: Navigation, roles: ['admin', 'rider', 'support'] },
    { name: 'Generar QR', href: '/qr-generator', icon: QrCode, roles: ['admin', 'dispensary_owner', 'dispensary_supervisor', 'dispensary_preparer', 'support'] },
    { name: 'Escanear QR', href: '/qr-scanner', icon: ScanLine, roles: ['admin', 'dispensary_owner', 'dispensary_supervisor', 'rider', 'support', 'customer'] },
    { name: 'Dispensarios', href: '/dispensaries', icon: Store, roles: ['admin', 'dispensary_owner'] },
    { name: 'Riders', href: '/riders', icon: Bike, roles: ['admin', 'support'] },
    { name: 'Reembolsos', href: '/refunds', icon: DollarSign, roles: ['admin', 'support', 'dispensary_owner'] }
  ];

  const visibleNav = navigation.filter(item => item.roles.includes(user.role));

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EAE0D5', margin: 0, padding: 0 }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-50 shadow-md" style={{ backgroundColor: '#0E2A24', marginTop: 0, paddingTop: 0 }}>
        <div className="px-4 py-3 flex justify-between items-center" style={{ paddingTop: '12px' }}>
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? (
                <X className="w-6 h-6" style={{ color: '#4ADE80' }} />
              ) : (
                <Menu className="w-6 h-6" style={{ color: '#4ADE80' }} />
              )}
            </button>
            <a href="/" className="flex items-center gap-1.5">
              <img 
                src="/uid-text.png" 
                alt="uid" 
                className="h-7"
              />
              <img 
                src="/uid-icon.png" 
                alt="UID" 
                className="h-7"
                data-testid="header-logo"
              />
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="/profile" className="flex items-center gap-2 hover:opacity-80 transition cursor-pointer">
              {user.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover border-2"
                  style={{ borderColor: '#4ADE80' }}
                />
              ) : (
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4ADE80' }}>
                  <User className="w-5 h-5" style={{ color: '#0E2A24' }} />
                </div>
              )}
              <div className="hidden md:block">
                <p className="text-sm font-semibold" style={{ color: '#4ADE80' }}>{user.name}</p>
                <p className="text-xs" style={{ color: '#9CA3AF' }}>{user.role.replace('_', ' ')}</p>
              </div>
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              style={{ borderColor: '#4ADE80', color: '#4ADE80' }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-[56px] left-0 h-[calc(100vh-56px)] w-64 shadow-lg transform transition-transform duration-300 z-40 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
          style={{ backgroundColor: '#0E2A24' }}
          data-testid="sidebar"
        >
          <nav className="sidebar-nav p-4 space-y-2 h-full overflow-y-auto">{visibleNav.map((item) => {
              const Icon = item.icon;
              const isActive = window.location.pathname === item.href;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`nav-item flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                    isActive ? 'shadow-md' : 'hover:bg-white/5'
                  }`}
                  style={{
                    backgroundColor: isActive ? '#4ADE80' : 'transparent',
                    color: isActive ? '#0E2A24' : '#FFFFFF'
                  }}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </a>
              );
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-overlay"
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;